/** Support - how to reach us, whether the service is up, what to expect, and where the guides are
 * (v5 stage F4; ADR-0212).
 *
 * **It is an email contact, and the page says so as data.** There is no ticket queue in this product
 * (P-08 moved support cases to a separate internal application that needs its own ADR before anything
 * of it is built, because it is the one surface that sees across tenants, ADR-0041). So "Write to
 * support" is a link that opens the person's own email program with the fields a good report needs
 * already in the body - nothing is sent from here, no ticket number is invented, and no support
 * hours, named agent or uptime percentage appears anywhere: the v5 prototype's were fabricated
 * (`docs/design/demo-to-api-gap.md`). The one number that may appear is the reply time KD decided on
 * 2026-11-03 (D-18, "within 2 working days"), and it is read from the deployment's settings
 * (`replyPromise`), never typed here; with none set the tile says "Not promised". A number nobody
 * set or measured is the thing this product refuses
 * to print. The steps and the "what to expect" are behind the info controls (`infoText.ts`, section
 * `support`); the tiles say the same facts as data.
 *
 * **Status is what was measured just now.** Whether this page could read the deployment's settings is
 * a real round trip to the API, said with its outcome; the scheduled work's liveness is the existing
 * panel (Admin and Approver); and the missing thing - an uptime history - is a tile that says *not
 * measured*, so its absence is not read as 100%.
 *
 * The address is configuration (`contact.ts`); when none has been decided the tile says so and there
 * is no button, because mail to a mailbox nobody reads is worse than being told there is none.
 */

import { Envelope, Lifebuoy } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";

import { useConfig } from "../api/config";
import { GridTable, HowDrawer, StatusChip, TablePage, Tile, TileStrip } from "../components/ui";
import type { GridColumn } from "../components/ui";
import { useSupportEmail, useSupportReplyPromise } from "../shell/contact";
import type { Guide } from "../shell/guidesContent";
import { GUIDES } from "../shell/guidesContent";
import { RequireSession } from "../shell/RequireSession";
import { LivenessPanel } from "./LivenessPanel";
import { keepSearch } from "./search";
import { ScreenFrame } from "./ScreenFrame";
import { SystemDisclosure } from "./SystemDisclosure";
import own from "./Support.module.css";
import { useScreen } from "./useScreen";

/** What a report needs, so the first reply is not a list of questions. No personal data: the receipt
 * on a figure already names the document it came from, and that is enough to find it. */
const TEMPLATE = [
  "Screen (the address bar):",
  "What I was doing:",
  "What I expected:",
  "What happened instead:",
  "Reference shown with the error, if any:",
];

function mailto(address: string): string {
  const subject = encodeURIComponent("EmitIQ support request");
  const body = encodeURIComponent(`${TEMPLATE.join("\n\n")}\n`);
  return `mailto:${address}?subject=${subject}&body=${body}`;
}

const GUIDE_COLUMNS: GridColumn<Guide>[] = [
  { id: "guide", header: "Guide", cell: (guide) => guide.title },
  { id: "for", header: "For", cell: (guide) => guide.audience },
  {
    id: "state",
    header: "State",
    cell: () => (
      <StatusChip tone="neutral" size="sm">
        Draft
      </StatusChip>
    ),
  },
];

function SupportBody() {
  const screen = useScreen();
  const address = useSupportEmail();
  const promise = useSupportReplyPromise();
  const config = useConfig();
  const carry = keepSearch(screen);

  return (
    <ScreenFrame screen={screen} title="Support" crumb="how to reach us, and what to expect">
      <TablePage
        icon={Lifebuoy}
        title="Support"
        label="Support"
        actions={
          <span className={own.actions}>
            <HowDrawer infoKey="support.how" label="How to raise a ticket" />
            {address ? (
              <a className={own.write} href={mailto(address)} data-testid="support-write">
                <Envelope size={16} aria-hidden="true" />
                Write to support
              </a>
            ) : (
              <span data-testid="support-write-none">
                <StatusChip tone="neutral" size="sm">
                  No address published
                </StatusChip>
              </span>
            )}
          </span>
        }
        tiles={
          <TileStrip label="Support at a glance">
            <Tile
              value={address ? "Published" : "Not published yet"}
              label="Email"
              testId="support-email"
              link={
                address ? (
                  <a href={`mailto:${address}`}>{address}</a>
                ) : (
                  // No address is decided yet (a decision for KD, not the product): say where the
                  // answer to "who do I ask" is, rather than leave a stuck person with a dead end.
                  <Link to="/help" search={{ ...carry, tab: "contact" }} data-testid="support-who-to-ask">
                    Who to ask
                  </Link>
                )
              }
              infoKey="support.expect"
            />
            <Tile
              value={config.isPending ? "Checking" : config.error ? "Could not be reached" : "Answering"}
              label="This service"
              testId="support-status-service"
              link={
                config.error ? (
                  <button type="button" className={own.retry} onClick={() => void config.refetch()}>
                    Try again
                  </button>
                ) : undefined
              }
            />
            <Tile value={promise ?? "Not promised"} label="Reply time" testId="support-reply" />
            <Tile value="Not measured" label="Uptime history" testId="support-uptime" />
            <Tile
              value={String(GUIDES.length)}
              label="Guides in draft"
              link={
                <Link to="/privacy-requests" search={carry}>
                  Your data requests
                </Link>
              }
            />
          </TileStrip>
        }
        table={
          <GridTable
            caption="Customer guides"
            columns={GUIDE_COLUMNS}
            rows={GUIDES}
            rowKey={(guide) => guide.id}
            rowNumbers={false}
            testId="support-guides"
          />
        }
      />
      {/* The scheduled work's liveness is a panel of its own, so it sits behind a disclosure: opened, it
          is the same panel (an Admin or Approver sees it; another role is told who does). */}
      <SystemDisclosure summary="Service checks: has the scheduled work run" testId="support-liveness" lazy>
        <LivenessPanel />
      </SystemDisclosure>
    </ScreenFrame>
  );
}

export function SupportScreen() {
  return (
    <RequireSession>
      <SupportBody />
    </RequireSession>
  );
}
